import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import EntityDetailPopup from '../EntityDetailPopup'
import { popupClasses } from '../constants';
import { MenuItem, TextField } from '@mui/material';
import { DownArrowIcon } from '../CustomSvgIcons';
import { FRTLContext } from '../../../context/FRTLContext';

const keys = ['farm', 'room', 'table', 'level'];

export default function CropRunFRTLPopup(props) {
    const {
        open,
        onClose,
        // isMetrcRoutine,
        metrcStatus,
        selectedFarmId,
        currDarkThemeClass,
        currentRoom,
        ...otherProps
    } = props;

    const [values, setValues] = useState({
        ...otherProps,
    });

    // Check if fields should be disabled based on METRC status
    const isMetrcDisabled = useMemo(() => {
        return metrcStatus === 'metrc_validated' || metrcStatus === 'metrc_synced' || metrcStatus === 'complete';
    }, [metrcStatus]);

    const handleSetValues = useCallback((prop, value) => {
        setValues(state => ({ ...state, [prop]: value }))
    }, []);

    const { frtlContextValue, setFrtlContextValue } = useContext(FRTLContext);
    
    const handleFRTLNewDepsRes = useCallback(() => {
        if (!frtlContextValue){
            console.log('FRTL Context Value is not available.');
            return;
        }

        const { farms = [], rooms = [], tables = [], levels = [] } = frtlContextValue;

        // Add "All Rooms" option if not present and no currentRoom
        if (!currentRoom && !rooms.some(room => room.id === 0)) {
            rooms.unshift({ id: 0, room_name: 'All Rooms', farm_id: 0 });
        }

        // Add "All Tables" option if not present
        if (!tables.some(table => table.id === 0)) { 
            tables.unshift({ id: 0, name: 'All Tables', room_id: 0 });
        }

        // Add "All Levels" option if not present
        if (!levels.some(level => level.id === 0)) { 
            levels.unshift({ id: 0, name: 'All Levels', roomtable_id: 0 });
        }

        const payload = {
            farms,
            rooms,
            tables,
            levels
        };

        if (selectedFarmId) {
            const selectedFarm = farms.find(farm => farm.id === selectedFarmId);
            if (selectedFarm) {
                payload.farm = selectedFarm.id;
            }
        }

        if (currentRoom) {
            const roomExists = rooms.find(room => room.id === currentRoom.id);
            if (roomExists) { 
                payload.room = roomExists.id;
            }
        }

        setValues(state => ({
            ...state,
            ...payload
        }));
    }, [frtlContextValue, selectedFarmId, currentRoom, setFrtlContextValue]);

    // Auto-select the only room if there's just one available
    useEffect(() => {
        if (values.farm && values.rooms && !isMetrcDisabled) {
            const availableRooms = values.rooms.filter(e => e.farm_id === values.farm && e.id !== 0);
            
            if (availableRooms.length === 1 && !values.room) {
                handleSetValues('room', availableRooms[0].id);
            }
        }
    }, [values.farm, values.rooms, values.room, isMetrcDisabled]);

    // Auto-select the only table if there's just one available
    useEffect(() => {
        if (values.room && values.tables && !isMetrcDisabled) {
            const availableTables = values.tables.filter(e => e.room_id === values.room && e.id !== 0);
            
            if (availableTables.length === 1 && !values.table) {
                handleSetValues('table', availableTables[0].id);
            }
        }
    }, [values.room, values.tables, values.table, isMetrcDisabled]);


    useEffect(() => {
      if (values.table && values.levels && !isMetrcDisabled) {
        const availableLevels = values.levels.filter(e => e.roomtable_id === values.table && e.id !== 0);
  
        if (availableLevels.length === 1 && !values.level) {
          handleSetValues('level', availableLevels[0].id);
        }
      }
    }, [values.table, values.levels, values.level, isMetrcDisabled]);

    useEffect(() => {
        handleFRTLNewDepsRes();
    }, [frtlContextValue, selectedFarmId, handleFRTLNewDepsRes]);

    const toDisable = () => keys.some(key => values?.[key] === 0 ? false : !values?.[key]);

    const onSaveClick = () => {
        if (toDisable()) return;
        otherProps?.handleEditFrtl(`${values?.farm}.${values?.room}.${values?.table}.${values?.level}`);
        onClose();
    };

    const menuItemProps = value => ({
        className: `${popupClasses?.sensor?.menuItem} ${currDarkThemeClass}`,
        focusVisibleClassName: popupClasses?.sensor?.menuItemFocus,
        value: value,
    }); 

    const farmsList = useMemo(() => {
        let farms = values?.farms || [];
        
        if (selectedFarmId) {
            farms = farms.filter(farm => farm.id === selectedFarmId);
        }

        return farms?.map((farm, index) => (
            <MenuItem key={index} {...menuItemProps(farm?.id)}>
                {`Farm (${farm?.farm_alias}) - ${farm?.farm_name}`}
            </MenuItem> 
        ));
    }, [values?.farms, selectedFarmId]);

    const getFarmDisplay = useMemo(() => {
        if (selectedFarmId && values?.farms) {
            const farm = values.farms.find(f => f.id === selectedFarmId);
            return farm ? `Farm (${farm.farm_alias}) - ${farm.farm_name}` : '';
        }
        return '';
    }, [selectedFarmId, values?.farms]);

    const getRoomDisplay = useMemo(() => {
        if (currentRoom && values?.rooms) {
            const room = values.rooms.find(r => r.id === currentRoom);
            return room ? `Room (${room.room_alias}) - ${room.room_name}` : '';
        }
        return ''; 
    }, [currentRoom, values?.rooms]);

    const roomsList = useMemo(() => {
        let rooms = values?.rooms || [];
    
        if (values?.farm) {
            rooms = rooms.filter(e => e.farm_id === values.farm);
        } else if (selectedFarmId) {
            rooms = rooms.filter(e => e.farm_id === selectedFarmId);
        }
        
        // Filter out any entries with id === 0
        rooms = rooms.filter(room => room.id !== 0);
    
        return rooms.map((room, index) => (
            <MenuItem key={index} {...menuItemProps(room.id)}>
                {`Room (${room.room_alias}) - ${room.room_name}`}
            </MenuItem>
        ));
    }, [values?.rooms, values?.farm, selectedFarmId]);

    const tablesList = useMemo(() => {
        let tables = values?.tables || [];
  
        if (values?.room !== '' && values?.room) {
            tables = tables?.filter(e => e?.room_id === values?.room);
        }
        
        tables = tables.filter(table => table.id !== 0);
  
        return tables?.map((table, index) => (
          <MenuItem key={index} {...menuItemProps(table?.id)}>{`Table (${table?.table_alias}) - ${table?.name}`}</MenuItem>
        ));
    }, [values?.tables, values?.room]);
    
    const levelsList = useMemo(() => {
      let levels = values?.levels || [];
      if (values?.table !== '' && values?.table) {
        levels = levels.filter(e => e.roomtable_id === values.table);
      }
  
      // Remove "All Levels" (id=0) if real levels exist
      levels = levels.filter(level => level.id !== 0);
  
      return levels?.map((level, index) => (<MenuItem key={index} {...menuItemProps(level?.id)}>{level?.id !== 0 ? `Level (${level?.level_alias}) - ${level?.name}` : `${level?.name}`}</MenuItem>));
    }, [values?.levels, values?.table, values?.room]);
    
    const aSTextFieldProps = ({ prop, inputProps = {}, otherTextProps = {} }) => ({
        autoComplete: 'off',
        className: `${popupClasses?.sensor?.textField} ${currDarkThemeClass}`,
        disabled: false,
        fullWidth: true,
        inputProps,
        label: ' ',
        onChange: ({ target: { value } }) => handleSetValues(prop, value),
        required: true,
        value: values?.[prop] ?? '',
        ...otherTextProps,
    });

    const EntityDetailPopupProps = {
        open,
        onClose,
        toDisableSave: toDisable(),
        title: 'FRTL Selection',
        headContentText: 'Please select the appropriate FRTL values of your choice',
        currDarkThemeClass,
        onSaveClick
    };

    const fFarmProps = {
        otherTextProps: {
            label: 'Farm',
            select: !selectedFarmId,
            SelectProps: selectedFarmId ? undefined : {
                IconComponent: !!values?.farm ? null : DownArrowIcon,
            },
            value: values?.farm || '',
            disabled: true // Farm is always disabled as mentioned in comment
        },
        prop: 'farm',
    };

    const fRoomProps = {
        otherTextProps: {
            label: 'Room',
            select: !currentRoom,
            SelectProps: {
                IconComponent: DownArrowIcon,
            },
            value: values?.room ?? '',
            onChange: ({ target: { value } }) => {
                if (value === 0) {
                    handleSetValues('room', value);
                    handleSetValues('table', value);
                    handleSetValues('level', value);
                } else {
                    handleSetValues('room', value);
                }
            },
            disabled: !values?.farm || isMetrcDisabled, // Disable if METRC validated/synced
        },
        prop: 'room'
    };

    const tableCount = tablesList?.length || 0;

    const fTableProps = {
        otherTextProps: {
            label: 'Table',
            select: true,
            SelectProps: {
                IconComponent: tableCount === 1 ? () => null : DownArrowIcon,
            },
            value: values?.room === 0 ? 0 : values?.table ?? '',
            onChange: ({ target: { value } }) => {
                handleSetValues('table', value);
                handleSetValues('level', '');
            },
            disabled: tableCount === 1 || !values?.room || isMetrcDisabled, // Disable if METRC validated/synced
        },
        prop: 'table',
    };

    const levelCount = levelsList?.length || 0;

    const fLevelProps = {
        otherTextProps: {
            label: 'Level',
            select: true,
            SelectProps: {
                IconComponent: levelCount === 1 ? () => null : DownArrowIcon,
            },
            value: values?.room === 0 || values?.table === 0 ? 0 : values?.level ?? '',
            disabled: levelCount === 1 || !values?.room || !values?.table || isMetrcDisabled, // Disable if METRC validated/synced
        },
        prop: 'level',
    };

    return (
        <EntityDetailPopup {...EntityDetailPopupProps}>
            {selectedFarmId ? (
                <TextField {...aSTextFieldProps({
                    ...fFarmProps,
                    otherTextProps: {
                        ...fFarmProps.otherTextProps,
                        value: getFarmDisplay,
                    }
                })} />
            ) : (
                <TextField {...aSTextFieldProps(fFarmProps)}>{farmsList}</TextField>
            )}
            <TextField {...aSTextFieldProps({
                ...fRoomProps,
                otherTextProps: {
                    ...fRoomProps.otherTextProps,
                    value: currentRoom ? getRoomDisplay : values?.room ?? '',
                }
            })}>
                {roomsList}
            </TextField>
            <TextField {...aSTextFieldProps(fTableProps)}>{tablesList}</TextField>
            <TextField {...aSTextFieldProps(fLevelProps)}>{levelsList}</TextField>
        </EntityDetailPopup>
    );
}